/**
 * Product Quote Modal
 * Handles opening/closing and form submission for product quote requests
 */

class ProductQuoteModal {
  constructor() {
    this.modal = document.querySelector('[data-product-quote-modal]');
    this.form = document.querySelector('[data-product-quote-form]');
    this.triggers = document.querySelectorAll('[data-product-quote-trigger]');
    this.closeButtons = document.querySelectorAll('[data-quote-modal-close]');
    this.messageContainer = document.querySelector('[data-form-message]');
    this.submitButton = this.form?.querySelector('[type="submit"]');
    this.submitText = this.submitButton?.querySelector('[data-submit-text]');
    this.spinner = this.submitButton?.querySelector('[data-spinner]');

    console.log('ProductQuoteModal initialized', {
      modal: this.modal,
      form: this.form,
      triggers: this.triggers.length,
      closeButtons: this.closeButtons.length
    });

    if (!this.modal || !this.form) {
      console.error('Modal or form not found!');
      return;
    }

    this.init();
  }

  init() {
    console.log('Initializing event listeners...');

    // Open modal when trigger buttons are clicked
    this.triggers.forEach((trigger, index) => {
      console.log(`Adding click listener to trigger ${index}:`, trigger);
      trigger.addEventListener('click', (e) => {
        console.log('Trigger clicked!', e.target);
        this.openModal();
      });
    });

    // Close modal when close buttons are clicked
    this.closeButtons.forEach(button => {
      button.addEventListener('click', () => this.closeModal());
    });

    // Close modal when clicking outside
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeModal();
      }
    });

    // Handle form submission
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Close modal on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.open) {
        this.closeModal();
      }
    });
  }

  openModal() {
    if (this.modal) {
      try {
        this.modal.showModal();
        document.body.style.overflow = 'hidden';
      } catch (error) {
        console.error('Error opening modal:', error);
      }
    }
  }

  closeModal() {
    if (this.modal) {
      this.modal.close();
      document.body.style.overflow = '';
      this.resetForm();
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    if (!this.form) return;

    this.hideMessage();

    // Collect form data
    const formData = {
      first_name: this.form.querySelector('[name="contact[First_name]"]')?.value.trim() || '',
      last_name: this.form.querySelector('[name="contact[Last_name]"]')?.value.trim() || '',
      email: this.form.querySelector('[name="contact[email]"]')?.value.trim() || '',
      phone: this.form.querySelector('[name="phone"]')?.value.trim() || 'Not provided',
      zip: this.form.querySelector('[name="contact[zip]"]')?.value.trim() || '',
      description: this.form.querySelector('[name="contact[body]"]')?.value.trim() || '',
      productTitle: this.form.querySelector('[name="contact[product_title]"]')?.value || '',
      selectedColor: this.form.querySelector('#SelectedColor')?.value || '',
      selectedXP: this.form.querySelector('#SelectedXP')?.value || '',
      userSelection: this.form.querySelector('#UserSelection')?.value || '',
      vin: this.form.querySelector('[name="contact[vin]"]')?.value || '',
    };

    console.log('📩 Captured Form Data:', formData);

    try {
      // Show loading state only after validation passes
      this.setLoadingState(true);

      // Check if this is a vehicle configurator product
      const isVehicleConfigurator = document.querySelector('.vehicle-configurator') !== null;

      if (isVehicleConfigurator) {
        // Vehicle configurator product - use /api/submitForm endpoint
        console.log('🚗 Sending data to Azure API (Vehicle Configurator)...');

        const zohoResponse = await fetch('https://shopify-zoho-integration-api.azurewebsites.net/api/submitForm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        const zohoData = await zohoResponse.json();

        if (!zohoResponse.ok) {
          console.error('❌ Zoho API Error:', zohoData);
          throw new Error('Failed to submit the form to Zoho');
        }

        console.log('✅ Zoho API Response:', zohoData);
        console.log('📧 Sending data to Klaviyo...');

        const klaviyoResponse = await fetch('https://ecomklaviyoapi.azurewebsites.net/api/klaviyoapi', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        const klaviyoData = await klaviyoResponse.json();

        if (!klaviyoResponse.ok) {
          console.error('⚠️ Klaviyo API Error:', klaviyoData);
        }

        console.log('✅ Klaviyo API Response:', klaviyoData);
      } else {
        // Other products - use /api/request-form endpoint
        console.log('📦 Sending data to Azure API (Standard Product)...');

        const zohoResponse = await fetch('https://shopify-zoho-integration-api.azurewebsites.net/api/request-form', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        const zohoData = await zohoResponse.json();

        if (!zohoResponse.ok) {
          console.error('❌ Zoho API Error:', zohoData);
          throw new Error('Failed to submit the form to Zoho');
        }

        console.log('✅ Zoho API Response:', zohoData);
      }

      // Reset form and redirect
      this.form.reset();
      this.setLoadingState(false);

      setTimeout(() => {
        window.location.href = 'https://vehicles.ridefox.com/pages/thanks';
      }, 500);

    } catch (error) {
      console.error('❌ Quote form error:', error);
      this.showMessage('Sorry, there was an error submitting your request. Please try again.', 'error');
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    if (!this.submitButton || !this.submitText || !this.spinner) return;

    this.submitButton.disabled = isLoading;

    if (isLoading) {
      this.submitText.style.display = 'none';
      this.spinner.removeAttribute('hidden');
    } else {
      this.submitText.style.display = '';
      this.spinner.setAttribute('hidden', '');
    }
  }

  showMessage(message, type) {
    if (!this.messageContainer) return;

    this.messageContainer.textContent = message;
    this.messageContainer.className = `product-quote-form__message product-quote-form__message--${type}`;
    this.messageContainer.removeAttribute('hidden');
  }

  hideMessage() {
    if (!this.messageContainer) return;
    this.messageContainer.setAttribute('hidden', '');
  }

  resetForm() {
    if (!this.form) return;
    this.form.reset();
    this.hideMessage();
  }
}

// Initialize when DOM is ready
function initProductQuoteModal() {
  const modal = document.querySelector('[data-product-quote-modal]');
  const triggers = document.querySelectorAll('[data-product-quote-trigger]');

  if (modal) {
    new ProductQuoteModal();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductQuoteModal);
} else {
  initProductQuoteModal();
}
